import i18n from '@/utils/i18n';
import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { PricingEdit } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import {
  UpsertPriceDraftForBusinessOrganizationInput,
  UpsertPricingPriceDraftUpdatesForBusinessOrganizationMutation,
} from '@/types';
import { useCallback } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { usePriceEditsTransformer } from '@sales-portal/domain/pricing/blocs/transformers/use-price-edits-transformer';
import { usePricingEditsStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { useUpsertPricingPriceDraftUpdatesForBusinessOrganizationMutation } from '@sales-portal/domain/pricing/blocs/documents/update-price-draft.generated';

const useUpdatePriceDraftMutator = (options?: {
  onCompleted?: (
    data: UpsertPricingPriceDraftUpdatesForBusinessOrganizationMutation,
  ) => void;
}) => {
  const [updatePricingPriceDraftMutation, { data, loading, error }] =
    useUpsertPricingPriceDraftUpdatesForBusinessOrganizationMutation({
      onCompleted: options?.onCompleted,
    });
  const { showErrorToast } = useExperienceToasts();

  const updatePriceDraft = useCallback(
    async (variantPacks: UpsertPriceDraftForBusinessOrganizationInput[]) => {
      try {
        return await updatePricingPriceDraftMutation({
          variables: {
            variantPacks,
          },
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : i18n.t('pricing.verify_edits.unknown_error');
        showErrorToast(errorMessage);
      }
    },
    [showErrorToast, updatePricingPriceDraftMutation],
  );

  return {
    data,
    error,
    loading,
    updatePriceDraft,
  };
};

export const useSyncAndVerifyPriceDraftEdits = () => {
  const pricingEdits = usePricingEditsStore((state) => state.edits);
  const transformPriceEdits = usePriceEditsTransformer();

  const { updatePriceDraft, data, loading, error } = useUpdatePriceDraftMutator(
    {
      onCompleted: (mutationData) => {
        const result =
          mutationData?.upsertPricingPriceDraftUpdatesForBusinessOrganization;
        if (!result) {
          return;
        }

        const localEditsMap = new Map(
          pricingEdits.map((edit) => [buildAccountKey(edit), edit]),
        );

        const pricingEditsToHydrate = result.cost_changes.reduce<PricingEdit[]>(
          (acc, change) => {
            const key = buildAccountKey({
              restaurantUUID: change.restaurant?.uuid ?? '',
              variantPackUUID: change.variant_pack?.uuid ?? '',
            });
            const edit = localEditsMap.get(key);

            if (edit) {
              acc.push(edit);
            }

            return acc;
          },
          [],
        );

        // Replace edits with server-validated ones without touching undo history.
        usePricingEditsStore.setState({ edits: pricingEditsToHydrate });
      },
    },
  );

  const syncAndVerify = useCallback(async () => {
    if (pricingEdits.length === 0) {
      return;
    }

    const variantPacksInput = transformPriceEdits(pricingEdits);
    return updatePriceDraft(variantPacksInput);
  }, [pricingEdits, transformPriceEdits, updatePriceDraft]);

  return {
    data,
    error,
    loading,
    syncAndVerify,
  };
};
